import React, { useState, useEffect } from 'react';
import axiosClient from '../utils/axiosClient';
import { 
    BiVideo, 
    BiUpload, 
    BiLink, 
    BiCheckCircle, 
    BiTrash, 
    BiPlayCircle,
    BiPlus,
    BiLoaderAlt,
    BiErrorCircle
} from 'react-icons/bi';

const QuickStartVideoManagement = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Form State
    const [title, setTitle] = useState('');
    const [videoType, setVideoType] = useState('link'); // 'link' or 'upload'
    const [videoUrl, setVideoUrl] = useState('');
    const [videoFile, setVideoFile] = useState(null);

    const fetchVideos = async () => {
        try {
            setLoading(true);
            const response = await axiosClient.get('/walkthrough/all');
            setVideos(response.data);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch videos:", err);
            setError("Could not load videos. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideos();
    }, []);

    const handleUpload = async (e) => {
        e.preventDefault();
        setUploading(true);
        setError(null);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('video_type', videoType);
        
        if (videoType === 'link') {
            formData.append('video_url', videoUrl);
        } else if (videoFile) {
            formData.append('videoFile', videoFile);
        } else {
            setError("Please provide a video link or file.");
            setUploading(false);
            return;
        }

        try {
            await axiosClient.post('/walkthrough/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            // Reset form
            setTitle('');
            setVideoUrl('');
            setVideoFile(null);
            setVideoType('link');
            
            // Refresh list
            fetchVideos();
        } catch (err) {
            console.error("Upload failed:", err);
            setError("Failed to add video. Make sure it's a valid video file and under 100MB.");
        } finally {
            setUploading(false);
        }
    };

    const handleActivate = async (id) => {
        try {
            await axiosClient.put(`/walkthrough/${id}/activate`);
            fetchVideos();
        } catch (err) {
            console.error("Activation failed:", err);
            alert("Failed to activate video.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this video?")) return;
        
        try {
            await axiosClient.delete(`/walkthrough/${id}`);
            fetchVideos();
        } catch (err) {
            console.error("Deletion failed:", err);
            alert("Failed to delete video.");
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                    <BiVideo className="mr-2 text-sky-600" />
                    Quick Start Video Management
                </h1>
                <p className="text-gray-500 mt-1">Manage the walkthrough video shown to new admins.</p>
            </div>

            {/* Upload Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <BiPlus className="mr-1" /> Add New Video
                </h2>
                <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Video Title</label>
                            <input 
                                type="text" 
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-sky-500 outline-none"
                                placeholder="e.g., Welcome Walkthrough"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Source Type</label>
                            <div className="flex space-x-4">
                                <button 
                                    type="button"
                                    onClick={() => setVideoType('link')}
                                    className={`flex-1 py-2 rounded-lg border flex items-center justify-center ${videoType === 'link' ? 'bg-sky-50 border-sky-500 text-sky-700' : 'bg-white text-gray-600'}`}
                                >
                                    <BiLink className="mr-1" /> External Link
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setVideoType('upload')}
                                    className={`flex-1 py-2 rounded-lg border flex items-center justify-center ${videoType === 'upload' ? 'bg-sky-50 border-sky-500 text-sky-700' : 'bg-white text-gray-600'}`}
                                >
                                    <BiUpload className="mr-1" /> File Upload
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {videoType === 'link' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL (YouTube/Vimeo/Direct)</label>
                                <input 
                                    type="url" 
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-sky-500 outline-none"
                                    placeholder="https://..."
                                    required={videoType === 'link'}
                                />
                                <p className="text-xs text-gray-400 mt-1 italic">Use embed URLs for best results.</p>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Video File</label>
                                <input 
                                    type="file" 
                                    accept="video/*"
                                    onChange={(e) => setVideoFile(e.target.files[0])}
                                    className="w-full border rounded-lg px-3 py-2 outline-none"
                                    required={videoType === 'upload'}
                                />
                                <p className="text-xs text-gray-400 mt-1">Max size: 100MB. Format: MP4, WebM.</p>
                            </div>
                        )}
                        <div className="pt-2">
                            <button 
                                type="submit"
                                disabled={uploading}
                                className="w-full bg-sky-600 text-white py-2 rounded-lg font-semibold hover:bg-sky-700 transition flex items-center justify-center disabled:opacity-50"
                            >
                                {uploading ? (
                                    <>
                                        <BiLoaderAlt className="animate-spin mr-2" /> Uploading...
                                    </>
                                ) : (
                                    <>
                                        <BiUpload className="mr-2" /> Save Video
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
                {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center">
                    <BiErrorCircle className="mr-2" /> {error}
                </div>}
            </div>

            {/* List Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800">Available Videos</h3>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded-full text-gray-600 font-medium">{videos.length} Total</span>
                </div>
                
                {loading ? (
                    <div className="p-12 flex flex-col items-center text-gray-400">
                        <BiLoaderAlt className="animate-spin w-8 h-8 mb-2" />
                        <p>Loading videos...</p>
                    </div>
                ) : videos.length === 0 ? (
                    <div className="p-12 text-center text-gray-400">
                        <BiVideo className="w-12 h-12 mx-auto mb-2 opacity-20" />
                        <p>No videos added yet.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="text-xs uppercase text-gray-500 font-semibold border-b">
                                    <th className="p-4">Video Info</th>
                                    <th className="p-4 text-center">Type</th>
                                    <th className="p-4 text-center">Status</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {videos.map((video) => (
                                    <tr key={video.id} className={`hover:bg-gray-50 transition ${video.is_active ? 'bg-sky-50/30' : ''}`}>
                                        <td className="p-4">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-3 text-gray-400">
                                                    <BiPlayCircle className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-800">{video.title}</p>
                                                    <p className="text-xs text-gray-500 truncate max-w-xs">{video.video_url}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${video.video_type === 'link' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                                                {video.video_type}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center">
                                            {video.is_active ? (
                                                <span className="inline-flex items-center text-green-600 font-bold text-sm">
                                                    <BiCheckCircle className="mr-1" /> Active
                                                </span>
                                            ) : (
                                                <button 
                                                    onClick={() => handleActivate(video.id)}
                                                    className="text-xs text-sky-600 font-semibold hover:underline"
                                                >
                                                    Set Active
                                                </button>
                                            )}
                                        </td>
                                        <td className="p-4 text-right">
                                            <button 
                                                onClick={() => handleDelete(video.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                                                title="Delete Video"
                                            >
                                                <BiTrash className="w-5 h-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuickStartVideoManagement;
